import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { TradeListResponse, updateTradeComment } from 'services/api/conversation';
import { toUpperCase } from 'utils/toLowerCase';
import { sellerMemosSupportOfficialPlatforms } from 'views/Conversation/Sider/constants';
import { OrderQueryParams } from './useOrderList';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';
import { ApiQueryKey } from 'services/config/queryKey';

export const useOrderNote = (order: Conversation.Trade.Vo) => {
  const { platform, editSellerMemoType, sellerMemos, lechatSellerMemos } = order;

  const isShopeeIn30daysMemo =
    toUpperCase(platform) === 'SHOPEE' && editSellerMemoType !== 'LECHAT';
  const enable = sellerMemosSupportOfficialPlatforms.includes(toUpperCase(platform));

  const note = useMemo(() => {
    //  Shopee 30天内的订单备注
    if (isShopeeIn30daysMemo) {
      return sellerMemos;
    } else {
      return lechatSellerMemos || sellerMemos;
    }
  }, [isShopeeIn30daysMemo, sellerMemos, lechatSellerMemos]);

  return {
    enable,
    isShopeeIn30daysMemo,
    note,
  };
};

interface OrderNoteMutationWithListProps {
  internalTradeId: Conversation.Trade.Vo['internalTradeId'];
  platform: Conversation.Trade.Vo['platform'];
  isShopeeIn30daysMemo: boolean;
  queryParams?: OrderQueryParams;
  onSuccess?: () => void;
  onError?: () => void;
}

export const useOrderNoteMutationWithList = ({
  internalTradeId,
  platform,
  isShopeeIn30daysMemo,
  queryParams,
  onSuccess,
  onError,
}: OrderNoteMutationWithListProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslate();

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (sellerMemos: string) => {
      return updateTradeComment({
        internalTradeId: internalTradeId,
        sellerMemos,
        platform: toUpperCase(platform),
        editSellerMemoType: isShopeeIn30daysMemo ? undefined : 'LECHAT',
      });
    },
    onMutate: async (newSellerMemos) => {
      if (!queryParams) return;

      const queryKey = [ApiQueryKey.OrderList, queryParams];

      // 取消正在进行的查询，避免覆盖乐观更新
      await queryClient.cancelQueries({ queryKey });

      // 保存之前的数据用于回滚
      const previousData = queryClient.getQueryData(queryKey);

      // 乐观更新缓存
      queryClient.setQueryData<InfiniteData<TradeListResponse>>(
        queryKey,
        (old: InfiniteData<TradeListResponse> | undefined) => {
          if (!old?.pages) return old;

          return {
            ...old,
            pages: old.pages.map((page) => {
              return {
                ...page,
                tradeList: page.tradeList?.map((order: Conversation.Trade.Vo) => {
                  if (order.internalTradeId === internalTradeId) {
                    return {
                      ...order,
                      sellerMemos: isShopeeIn30daysMemo ? newSellerMemos : undefined,
                      lechatSellerMemos: isShopeeIn30daysMemo ? undefined : newSellerMemos,
                    };
                  } else {
                    return order;
                  }
                }),
              };
            }),
          };
        },
      );

      return { previousData, queryKey };
    },
    onSuccess: () => {
      message.success(t('trade.updateCommentSucceed'));
      onSuccess?.();
    },
    onError: (_err, _newSellerMemos, context) => {
      // 回滚到之前的数据
      if (context?.previousData && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      message.error(t('trade.updateCommentFailed'));
      onError?.();
    },
  });

  return {
    mutateAsync,
    isLoading,
  };
};
